import type { Workflow } from "@/types/aiToolkit";

/** Template: Fireflies meeting → parallel extract → merge → ClickUp + KB */
export const MEETING_TO_CLICKUP_WORKFLOW: Workflow = {
  id: "workflow-template-meeting-clickup",
  name: "Meeting → ClickUp (Template)",
  description: "Parallel extraction from meeting transcript, then create ClickUp tasks and KB doc.",
  tier: "workflow",
  access: "pro",
  icon: "GitBranch",
  color: "bg-blue-500",
  category: "Communications",
  tags: ["fireflies", "clickup", "template"],
  trigger: { type: "event", config: { eventType: "fireflies.transcript" } },
  entryNodeId: "trigger-1",
  nodes: [
    { id: "trigger-1", type: "trigger", position: { x: 50, y: 200 }, data: { label: "Fireflies Transcript" } },
    { id: "knowledge-1", type: "knowledge", position: { x: 200, y: 200 }, data: { label: "Load Transcript", knowledgeSource: "trigger" } },
    { id: "parallel-1", type: "parallel", position: { x: 350, y: 200 }, data: { label: "Parallel Extract" } },
    { id: "agent-separate", type: "agent", position: { x: 500, y: 80 }, data: { label: "Separate Chat", agentId: "agent-context-sanitizer", skillIds: ["skill-meeting-separate-chat"] } },
    { id: "agent-deliverables", type: "agent", position: { x: 500, y: 200 }, data: { label: "Deliverables", agentId: "agent-deliverable-architect", skillIds: ["skill-meeting-deliverables"] } },
    { id: "agent-business", type: "agent", position: { x: 500, y: 320 }, data: { label: "Business Extract", agentId: "agent-business-extractor", skillIds: ["skill-meeting-business-extract"] } },
    { id: "merge-1", type: "merge", position: { x: 650, y: 200 }, data: { label: "Merge", mergeStrategy: "all" } },
    { id: "entity-1", type: "entity-resolution", position: { x: 800, y: 200 }, data: { label: "Resolve Entities" } },
    { id: "quality-1", type: "quality-gate", position: { x: 950, y: 200 }, data: { label: "Quality Gate", qualityThreshold: 0.5 } },
    { id: "tool-clickup", type: "tool", position: { x: 1100, y: 150 }, data: { label: "Create ClickUp Tasks", toolId: "clickup", toolOperation: "create_task", toolInputMapping: { listId: "variables.listId", tasks: "nodeOutputs.agent-deliverables.parsed.deliverables" } } },
    { id: "tool-kb", type: "tool", position: { x: 1100, y: 250 }, data: { label: "Save KB Doc", toolId: "kb", toolOperation: "create_document", toolInputMapping: { title: "variables.subject", content: "nodeOutputs.merge-1.raw" } } },
    { id: "output-1", type: "output", position: { x: 1250, y: 200 }, data: { label: "Done" } },
  ],
  edges: [
    { id: "e1", source: "trigger-1", target: "knowledge-1" },
    { id: "e2", source: "knowledge-1", target: "parallel-1" },
    { id: "e3", source: "parallel-1", target: "agent-separate" },
    { id: "e4", source: "parallel-1", target: "agent-deliverables" },
    { id: "e5", source: "parallel-1", target: "agent-business" },
    { id: "e6", source: "agent-separate", target: "merge-1" },
    { id: "e7", source: "agent-deliverables", target: "merge-1" },
    { id: "e8", source: "agent-business", target: "merge-1" },
    { id: "e9", source: "merge-1", target: "entity-1" },
    { id: "e10", source: "entity-1", target: "quality-1" },
    { id: "e11", source: "quality-1", target: "tool-clickup", sourceHandle: "quality_pass" },
    { id: "e12", source: "tool-clickup", target: "tool-kb" },
    { id: "e13", source: "tool-kb", target: "output-1" },
  ],
  isBlueprint: true,
  status: "active",
  createdAt: new Date().toISOString(),
  createdBy: "system",
};

export const GMAIL_SPAM_WORKFLOW: Workflow = {
  id: "workflow-template-gmail-spam",
  name: "Gmail Spam Filter (Template)",
  description: "Classify email; if spam, ask before moving to trash.",
  tier: "workflow",
  access: "pro",
  icon: "GitBranch",
  color: "bg-red-500",
  category: "Communications",
  tags: ["gmail", "template"],
  trigger: { type: "event", config: { eventType: "gmail.new_message" } },
  entryNodeId: "trigger-1",
  nodes: [
    { id: "trigger-1", type: "trigger", position: { x: 50, y: 150 }, data: { label: "New Email" } },
    { id: "knowledge-1", type: "knowledge", position: { x: 200, y: 150 }, data: { label: "Load Email", knowledgeSource: "trigger" } },
    { id: "agent-1", type: "agent", position: { x: 350, y: 150 }, data: { label: "Spam Classifier", agentId: "agent-comms-gmail-spam", skillIds: ["skill-gmail-spam-classify"] } },
    { id: "condition-1", type: "condition", position: { x: 500, y: 150 }, data: { label: "Is spam?", condition: "spam", conditionValue: true } },
    { id: "review-1", type: "human-review", position: { x: 650, y: 80 }, data: { label: "Approve trash", description: "Spam detected — approve move to trash?" } },
    { id: "tool-trash", type: "tool", position: { x: 800, y: 80 }, data: { label: "Move to trash", toolId: "gmail", toolOperation: "trash", toolInputMapping: { messageId: "variables.id" } } },
    { id: "output-spam", type: "output", position: { x: 950, y: 80 }, data: { label: "Trashed" } },
    { id: "output-clean", type: "output", position: { x: 650, y: 220 }, data: { label: "Not spam" } },
  ],
  edges: [
    { id: "e1", source: "trigger-1", target: "knowledge-1" },
    { id: "e2", source: "knowledge-1", target: "agent-1" },
    { id: "e3", source: "agent-1", target: "condition-1" },
    { id: "e4", source: "condition-1", target: "review-1", sourceHandle: "true" },
    { id: "e5", source: "condition-1", target: "output-clean", sourceHandle: "false" },
    { id: "e6", source: "review-1", target: "tool-trash" },
    { id: "e7", source: "tool-trash", target: "output-spam" },
  ],
  isBlueprint: true,
  status: "active",
  createdAt: new Date().toISOString(),
  createdBy: "system",
};

export const GMAIL_TRIAGE_WORKFLOW: Workflow = {
  id: "workflow-template-gmail-triage",
  name: "Gmail Triage Pipeline (Template)",
  description: "Classify spam, then draft a reply for legitimate emails.",
  tier: "workflow",
  access: "pro",
  icon: "GitBranch",
  color: "bg-orange-500",
  category: "Communications",
  tags: ["gmail", "template", "triage"],
  trigger: { type: "event", config: { eventType: "gmail.new_message" } },
  entryNodeId: "trigger-1",
  nodes: [
    { id: "trigger-1", type: "trigger", position: { x: 50, y: 150 }, data: { label: "New Email" } },
    { id: "knowledge-1", type: "knowledge", position: { x: 200, y: 150 }, data: { label: "Load Email", knowledgeSource: "trigger" } },
    { id: "agent-spam", type: "agent", position: { x: 350, y: 150 }, data: { label: "Spam Classifier", agentId: "agent-comms-gmail-spam", skillIds: ["skill-gmail-spam-classify"] } },
    { id: "condition-1", type: "condition", position: { x: 500, y: 150 }, data: { label: "Is spam?", condition: "spam", conditionValue: true } },
    { id: "agent-reply", type: "agent", position: { x: 650, y: 220 }, data: { label: "Reply Drafter", agentId: "agent-comms-gmail-reply", skillIds: ["skill-gmail-reply-draft"] } },
    { id: "output-reply", type: "output", position: { x: 800, y: 220 }, data: { label: "Reply draft ready" } },
    { id: "output-spam", type: "output", position: { x: 650, y: 80 }, data: { label: "Spam — skipped" } },
  ],
  edges: [
    { id: "e1", source: "trigger-1", target: "knowledge-1" },
    { id: "e2", source: "knowledge-1", target: "agent-spam" },
    { id: "e3", source: "agent-spam", target: "condition-1" },
    { id: "e4", source: "condition-1", target: "output-spam", sourceHandle: "true" },
    { id: "e5", source: "condition-1", target: "agent-reply", sourceHandle: "false" },
    { id: "e6", source: "agent-reply", target: "output-reply" },
  ],
  isBlueprint: true,
  status: "active",
  createdAt: new Date().toISOString(),
  createdBy: "system",
};

export const WORKFLOW_TEMPLATES = [MEETING_TO_CLICKUP_WORKFLOW, GMAIL_SPAM_WORKFLOW, GMAIL_TRIAGE_WORKFLOW];
